import { db, storage } from '../firebase';
import { collection, getDocs, getDoc, doc, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, uploadString, getDownloadURL, deleteObject } from 'firebase/storage';
import { Video } from '../types';

// Default Bangkok video
export const getDefaultVideo = (): Video => {
  return {
    id: 'bangkok-default',
    title: 'Bangkok',
    storageUrl: '',
    downloadUrl: '/assets/video/Bangkok.mp4',
    thumbnailUrl: '/assets/video/Bangkok-thumbnail.jpg',
    category: ['default'],
    createdAt: new Date()
  };
};

// Generate a thumbnail from a video file
export const generateThumbnail = (videoFile: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    // Create a video element to load the video
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    // Create a URL for the video file
    const videoUrl = URL.createObjectURL(videoFile);
    video.src = videoUrl;
    
    // When video metadata is loaded, capture a frame
    video.onloadedmetadata = () => {
      // Seek to 25% of the video duration for a better thumbnail
      video.currentTime = video.duration * 0.25;
    };
    
    // When the video has seeked to the specified time
    video.onseeked = () => {
      try {
        // Create a canvas element to draw the video frame
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set dimensions (16:9 aspect ratio with reasonable size)
        canvas.width = 480;
        canvas.height = 270;
        
        // Draw the current frame to the canvas
        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert canvas to base64 data URL
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        
        // Clean up
        URL.revokeObjectURL(videoUrl);
        
        resolve(dataUrl);
      } catch (error) {
        reject(error);
      }
    };
    
    // Handle errors
    video.onerror = () => {
      URL.revokeObjectURL(videoUrl);
      reject(new Error('Failed to load video for thumbnail generation'));
    };
  });
};

export const getVideos = async (): Promise<Video[]> => {
  const videosCollection = collection(db, 'videos');
  const videosSnapshot = await getDocs(videosCollection);
  
  // Get Firebase videos
  const firebaseVideos = videosSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate()
  } as Video));

  return firebaseVideos;
};

// Get first video as Bangkok, then Firebase videos for shuffling
export const getInitialVideo = async (): Promise<Video> => {
  return getDefaultVideo();
};

export const getVideoById = async (id: string): Promise<Video | null> => {
  // Return default Bangkok video if requested by ID
  if (id === 'bangkok-default') {
    return getDefaultVideo();
  }
  
  const videoDoc = doc(db, 'videos', id);
  const videoSnapshot = await getDoc(videoDoc);
  
  if (!videoSnapshot.exists()) return null;
  
  return {
    id: videoSnapshot.id,
    ...videoSnapshot.data(),
    createdAt: videoSnapshot.data().createdAt?.toDate()
  } as Video;
};

export const uploadVideo = async (
  file: File, 
  metadata: Omit<Video, 'id' | 'storageUrl' | 'downloadUrl' | 'thumbnailUrl' | 'createdAt'>
): Promise<Video> => {
  try {
    // Generate thumbnail first
    const thumbnailDataUrl = await generateThumbnail(file);
    
    // Create a unique name base for both video and thumbnail
    const uniqueId = Date.now();
    const fileName = file.name;
    
    // Create a storage reference for the video
    const videoStorageRef = ref(storage, `videos/${uniqueId}_${fileName}`);
    
    // Upload video file to Firebase Storage
    await uploadBytes(videoStorageRef, file);
    
    // Get the video download URL
    const videoDownloadUrl = await getDownloadURL(videoStorageRef);
    
    // Create a storage reference for the thumbnail
    const thumbnailStorageRef = ref(storage, `thumbnails/${uniqueId}_${fileName}.jpg`);
    
    // Upload thumbnail to Firebase Storage
    // First, remove the data URL prefix (e.g., "data:image/jpeg;base64,")
    const base64String = thumbnailDataUrl.split(',')[1];
    await uploadString(thumbnailStorageRef, base64String, 'base64', {
      contentType: 'image/jpeg'
    });
    
    // Get the thumbnail download URL
    const thumbnailDownloadUrl = await getDownloadURL(thumbnailStorageRef);
    
    // Create record in Firestore with both video and thumbnail URLs
    const newVideo = {
      ...metadata,
      storageUrl: videoStorageRef.fullPath,
      downloadUrl: videoDownloadUrl,
      thumbnailUrl: thumbnailDownloadUrl,
      createdAt: new Date()
    };
    
    const docRef = await addDoc(collection(db, 'videos'), newVideo);
    
    return {
      id: docRef.id,
      ...newVideo
    } as Video;
  } catch (error) {
    console.error('Error in uploadVideo:', error);
    throw error;
  }
};

export const updateVideo = async (id: string, updates: Partial<Video>): Promise<void> => {
  const videoRef = doc(db, 'videos', id);
  await updateDoc(videoRef, {
    ...updates,
    updatedAt: new Date()
  });
};

export const deleteVideo = async (id: string): Promise<void> => {
  // Get the video to find the storage path
  const video = await getVideoById(id);
  if (!video) return;
  
  // Delete video from storage if it exists
  if (video.storageUrl) {
    const storageRef = ref(storage, video.storageUrl);
    await deleteObject(storageRef);
  }
  
  // Delete thumbnail from storage if it exists
  if (video.thumbnailUrl && !video.thumbnailUrl.startsWith('/assets/')) {
    // Extract the path from the URL or use a stored path
    const thumbnailPath = video.thumbnailUrl.includes('thumbnails/') 
      ? `thumbnails/${video.thumbnailUrl.split('thumbnails/')[1].split('?')[0]}`
      : `thumbnails/${video.storageUrl.split('videos/')[1]}.jpg`;
    
    try {
      const thumbnailRef = ref(storage, thumbnailPath);
      await deleteObject(thumbnailRef);
    } catch (error) {
      console.error('Error deleting thumbnail:', error);
      // Continue with deletion even if thumbnail deletion fails
    }
  }
  
  // Delete from Firestore
  await deleteDoc(doc(db, 'videos', id));
}; 